import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, switchMap, finalize, mergeMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { TokenService } from '../services/token.service';
import { MixpanelService } from '../services/mixpanel.service';
import { LogoutService } from '../services/logout.service';
import { HostLocationService } from '../services/hostLocation.service';
import { HelperFunctions } from '../../../app/utils/helper.functions';

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
  constructor(
    private authService: AuthService,
    private tokenService: TokenService,
    private mixpanelService: MixpanelService,
    private logOutService: LogoutService,
    private hostLocationService: HostLocationService,
    private helperFunctions: HelperFunctions,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();
    request = this.addAuthToken(request, token);
    request = this.addProjectAndEnvironment(request);

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if ((request as any).retry) {
          return throwError(() => new Error('Please log in to your account'));
        }
        if (request?.headers.get('grant-type') === 'refresh-token') {
          this.logOut().then(() => {
            return throwError(() => new Error('Please log in to your account'));
          });
        }
        if (error?.status !== 401) return throwError({ error: new Error(error.error.message) });

        return from(this.reAuthenticate()).pipe(
          switchMap((accessToken) => {
            const originalRequest: any = this.addAuthToken(request, accessToken);
            originalRequest.retry = true;
            if (!originalRequest) return throwError(() => new Error('Request failed'));
            return next.handle(originalRequest);
          }),
          catchError((err) => {
            return throwError(() => new Error(`Please log in to your account`));
          }),
        );
      }),
      finalize(() => {
        if ((request as any).retry) (request as any).retry = false;
      }),
    );
  }
  async reAuthenticate(): Promise<string> {
    return await this.tokenService.getRefreshToken().catch((err) => {
      return this.authService.getToken();
    });
  }
  async logOut() {
    await this.mixpanelService.reset();
    await this.logOutService.logOut();
  }
  addAuthToken(request: HttpRequest<any>, token: string) {
    if (!token) {
      return request;
    }
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  addProjectAndEnvironment(request: HttpRequest<any>) {
    const host = this.hostLocationService.getHost();
    const { environment, project } = this.helperFunctions.getEnvironmentAndProject(host);

    return request.clone({
      setHeaders: {
        'X-Environment': environment,
        'X-Project': project,
      },
    });
  }
}
